import { Component, ElementRef, ViewChild, ViewEncapsulation } from '@angular/core';

// @ts-ignore
import * as THREE from 'three';
import * as OBC from '@thatopen/components';
import * as FRAGS from '@thatopen/fragments';
import * as BUI from '@thatopen/ui';
import * as OBF from '@thatopen/components-front';
import * as BUIC from '@thatopen/ui-obc';
import { PartialGanttOptions } from '@app/core/components/gantt/gantt.types';

@Component({
    selector: 'tt-jlr-test',
    templateUrl: './jlr-test.component.html',
    // template: ` <tt-grid [(ttOptions)]="accountingprocessingGrid"></tt-grid> `,
    styleUrls: ['./jlr-test.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class JlrTestComponent {
    private _showCriticalPath = false;
    private _showResourceGraph = false;

    public ganttOptions: PartialGanttOptions = this.createGanttOptions();

    public get showCriticalPath(): boolean {
        return this._showCriticalPath;
    }
    public set showCriticalPath(value: boolean) {
        this._showCriticalPath = value;
        this.updateGanttOptions({
            config: {
                criticalPath: value,
            },
        });
    }

    public get showResourceGraph(): boolean {
        return this._showResourceGraph;
    }
    public set showResourceGraph(value: boolean) {
        this._showResourceGraph = value;
        this.updateGanttOptions({
            resources: {
                enabled: [0, '0', false, 'false'].includes(value as any) ? false : true,
            },
        });
    }

    @ViewChild('container', { static: true })
    containerRef!: ElementRef<HTMLDivElement>;

    private components = new OBC.Components();
    private world!: OBC.World;
    private worlds!: OBC.Worlds;
    private ifcLoader!: OBC.IfcLoader;

    private highlighter!: OBF.Highlighter;

    ngOnInit() {
        if (this.containerRef?.nativeElement) {
            this.initWorld();
        }
    }

    ngOnDestroy() {
        this.components?.dispose();
    }

    private model?: FRAGS.FragmentsModel;

    private createGanttOptions(): PartialGanttOptions {
        return {
            editing: {
                links: true,
            },
            resources: {
                enabled: this.showResourceGraph,
            },
            config: {
                criticalPath: this.showCriticalPath,
            },
        };
    }

    private updateGanttOptions(options: PartialGanttOptions): void {
        this.ganttOptions = {
            ...this.ganttOptions,
            ...options,
            editing: {
                ...this.ganttOptions.editing,
                ...options.editing,
            },
            resources: {
                ...this.ganttOptions.resources,
                ...options.resources,
            },
            config: {
                ...this.ganttOptions.config,
                ...options.config,
            },
        };
    }

    log(event: any) {
        console.dir(event);
    }

    onSelect(event: FlatTreeNode[]) {
        console.log(event);
        this.selectedItem = this.model?.getItem(event[0].localId!);
        console.log(this.selectedItem.getAttributes());
        console.log(this.selectedItem.getCategory());
        console.log(this.selectedItem.getData());
        // console.log(this.selectedItem);
        // );
    }

    selectedItem?: any;

    async initWorld() {
        if (!this.containerRef?.nativeElement) return;

        BUI.Manager.init();

        this.components = new OBC.Components();

        this.worlds = this.components.get(OBC.Worlds);
        this.world = this.worlds.create<OBC.SimpleScene, OBC.OrthoPerspectiveCamera, OBF.PostproductionRenderer>();

        this.world.scene = new OBC.SimpleScene(this.components);
        (this.world.scene as OBC.SimpleScene).setup();
        // @ts-ignore
        this.world.scene.three.background = null;

        // this.components.get(OBC.Raycasters).get(this.world);
        // this.highlighter.config.world = this.world;
        // this.highlighter.s

        this.world.renderer = new OBF.PostproductionRenderer(this.components, this.containerRef.nativeElement);
        this.world.camera = new OBC.OrthoPerspectiveCamera(this.components);
        await this.world.camera.controls?.setLookAt(0, 0, 0, 26, -4, 25);

        this.components.init();

        // this.highlighter = this.components.get(OBF.Highlighter);
        // this.highlighter.setup({ world: this.world });
        // this.highlighter.zoomToSelection = true;
        this.components.get(OBC.Grids).create(this.world);
        this.ifcLoader = this.components.get(OBC.IfcLoader);
        this.ifcLoader.onIfcImporterInitialized.add((importer) => {
            console.log(importer.classes);
        });
        await this.ifcLoader.setup({
            autoSetWasm: false,
            wasm: {
                path: 'https://unpkg.com/web-ifc@0.0.72/',
                absolute: true,
            },
        });
        const workerUrl = '/assets/ifc/worker.mjs';
        const fragments = this.components.get(OBC.FragmentsManager);
        fragments.init(workerUrl);

        this.world.camera.controls?.addEventListener('rest', () => fragments.core.update(true));

        // Ensures that once the Fragments model is loaded
        // (converted from the IFC in this case),
        // it utilizes the world camera for updates
        // and is added to the scene.
        fragments.list.onItemSet.add(({ value: model }) => {
            model.useCamera(this.world.camera.three as THREE.OrthographicCamera | THREE.PerspectiveCamera);
            this.world.scene.three.add(model.object);
            fragments.core.update(true);
        });

        const casters = this.components.get(OBC.Raycasters);
        // Each raycaster is associated with a specific world.
        // Here, we retrieve the raycaster for the `world` used in our scene.
        const caster = casters.get(this.world);
        const marker = this.components.get(OBF.Marker);
        marker.threshold = 0;

        // for (let i = 0; i < 20; i++) {
        //     const x = Math.random() * 100 - 50;
        //     const y = Math.random() * 10;
        //     const z = Math.random() * 100 - 50;
        //     const element = BUI.Component.create(() => BUI.html`<bim-label style="font-size: 100px">🚀</bim-label>`);
        //     marker.create(this.world, element, new THREE.Vector3(x, y, z));
        // }

        const hoverer = this.components.get(OBF.Hoverer);
        hoverer.world = this.world;
        hoverer.enabled = true;
        hoverer.material = new THREE.MeshBasicMaterial({
            color: 0x6528d7,
            transparent: true, // transparent must be true to allow the animation
            opacity: 0.5, // this will act as the maximum possible opacity when animating
            depthTest: false, // recommended to avoid z-fighting
        });

        this.containerRef.nativeElement.addEventListener('dblclick', async () => {
            const result = await caster.castRay();

            console.dir(result);

            if (!!result) {
                const element = BUI.Component.create(() => BUI.html`<bim-label style="font-size: 20px">🚀</bim-label>`);

                marker.create(this.world, element, result.point);
            }
        });
        // this.containerRef.nativeElement.addEventListener('mousemove', async () => {
        //     const result: THREE.Intersection<any> | null = await caster.castRay();
        //     console.dir(result);
        //     if (!!result) {
        //         // @ts-ignore
        //         console.log('localId >>: ', result.localId);
        //         // @ts-ignore
        //         console.log('itemId >>: ', result.itemId);

        //         // @ts-ignore
        //         // (result.object as THREE.Object3D).material.color.setHex(0xff0000);
        //         // this.selectedId = '' + result.localId;
        //         // @ts-ignore
        //         // const modelIdMap = { [result.fragments.modelId]: new Set([result.localId]) };
        //         // console.log(modelIdMap);
        //         // const modelId = Object.keys(modelIdMap)[0];
        //         // if (modelId && fragments.list.get(modelId)) {
        //         //     const model = fragments.list.get(modelId)!;
        //         //     // @ts-ignore
        //         //     const [data] = await model.getItemsData([...modelIdMap[modelId]]);
        //         //     this.attributes = data;
        //         // }
        //         // const color = new THREE.Color('purple');

        //         // this.highlighter.highlightByID('hover', modelIdMap, true);

        //         // await fragments.highlight(
        //         //     {
        //         //         color,
        //         //         renderedFaces: FRAGS.RenderedFaces.ONE,
        //         //         opacity: 1,
        //         //         transparent: false,
        //         //     },
        //         //     modelIdMap
        //         // );

        //         // await fragments.core.update(true);
        //     }
        // });
    }

    selectedId: string = '';

    attributes: FRAGS.ItemData | undefined;
    async onFileSelected(event: any) {
        const file = event.target.files[0];
        if (!file) return;

        const buffer = await file.arrayBuffer();
        const data = new Uint8Array(buffer);

        this.model = await this.ifcLoader.load(data, true, file.name, {
            processData: {
                progressCallback: (progress) => console.log(progress),
            },
        });

        console.log(this.model);
        const spatialStructure = await this.model.getSpatialStructure();

        // console.dir(this.model.getItems());
        // console.dir(spatialStructure);
        // console.dir(buildFlatTreeWithItemData(spatialStructure, this.model));
        // this.tree = await buildFlatTreeWithItemData(spatialStructure, this.model);
        // // @ts-ignore
        this.world.camera.controls?.fitToBox((await this.model.getBoxes())[0], true);

        this.func();
        // this.func();
        // const group = model.root;

        // (this.world.scene.three as THREE.Scene).add(group);

        // // Fit camera
        // this.world.camera.controls?.fitToBox(group, false);
    }

    tree: FlatTreeNode[] = [];

    func() {
        window.addEventListener('keydown', (e) => {
            const controls = this.world.camera.controls!;
            const speed = 1.2; // adjust to taste

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            switch (e.key) {
                case 'ArrowUp': {
                    controls.truck(0, -speed, true); // move forward
                    break;
                }
                case 'ArrowDown': {
                    controls.truck(0, speed, true); // move backward
                    break;
                }
                case 'ArrowLeft': {
                    controls.truck(-speed, 0, true); // move left
                    break;
                }
                case 'ArrowRight': {
                    controls.truck(speed, 0, true); // move right
                    break;
                }
            }
        });
        BUI.Manager.init();

        /* MD
Now we will add some UI to play around with the actions in this tutorial. For more information about the UI library, you can check the specific documentation for it!
*/

        const [spatialTree] = BUIC.tables.spatialTree(
            {
                components: this.components,
                models: [this.model!],
            },
            true,
        );

        spatialTree.preserveStructureOnFilter = true;

        const [panel, updatePanel] = BUI.Component.create<BUI.PanelSection, {}>((_) => {
            // const onColorChange = ({ target }: { target: BUI.ColorInput }) => {
            //     color.set(target.color);
            // };

            // let nameLabel = BUI.html`<bim-label>There is no item name to display.</bim-label>`;
            // if (attributes && 'value' in attributes.Name) {
            //     nameLabel = BUI.html`<bim-label>${attributes.Name.value}</bim-label>`;
            // }

            // const onClearColors = async ({ target }: { target: BUI.Button }) => {
            //     target.loading = true;
            //     await fragments.resetHighlight();
            //     await fragments.core.update(true);
            //     target.loading = false;
            // };

            return BUI.html`
    <bim-panel active label="Raycasters Tutorial" class="options-menu">
      <bim-panel-section label="Controls">
        ${spatialTree}
        <bim-label>Double Click: Colorize element</bim-label>
      </bim-panel-section>
      <bim-panel-section label="Item Data">
        ${'nameLabel'}
      </bim-panel-section>
    </bim-panel>
  `;
        }, {});

        this.containerRef.nativeElement.append(panel);

        // onItemSelected = () => updatePanel();
    }
}

// Your spatial from model.getSpatialStructure()
export interface SpatialTreeItem {
    category: string | null;
    localId: number | null;
    children?: SpatialTreeItem[];
}

// What the tree-view will consume
export interface FlatTreeNode {
    id: string; // unique string id
    parentId: string | null;
    category: string | null; // null for elements
    localId: number | null; // null for pure category nodes
    label: string; // what you show in the UI
    isCategory: boolean;
}

export async function buildFlatTreeWithItemData(root: FRAGS.SpatialTreeItem, model: FRAGS.FragmentsModel): Promise<FlatTreeNode[]> {
    const flat: FlatTreeNode[] = [];
    const elementLocalIds: number[] = [];
    let nextId = 0;

    function walk(node: SpatialTreeItem, currentCategoryParentId: string | null) {
        const hasCategory = !!node.category;
        const hasChildren = !!node.children?.length;
        const hasLocalId = typeof node.localId === 'number';

        let thisId: string | null = null;

        // 1) Category node -> becomes its own row
        if (hasCategory) {
            thisId = String(nextId++);
            flat.push({
                id: thisId,
                parentId: currentCategoryParentId,
                category: node.category!,
                localId: node.localId ?? null, // usually null but not required
                label: node.category!, // display the category name
                isCategory: true,
            });

            // This is now the "closest parent with category"
            currentCategoryParentId = thisId;
        }

        // 2) Null-category node *with children* -> grouping only, skip itself
        if (!hasCategory && hasChildren) {
            for (const child of node.children!) {
                walk(child, currentCategoryParentId);
            }
            return;
        }

        // 3) Null-category leaf with localId -> actual element row under last category parent
        if (!hasCategory && hasLocalId) {
            const elementId = String(nextId++);
            flat.push({
                id: elementId,
                parentId: currentCategoryParentId,
                category: null,
                localId: node.localId!,
                label: '', // will be filled later from getItemsData
                isCategory: false,
            });
            elementLocalIds.push(node.localId!);
            return;
        }

        // 4) Category node with children: keep traversing
        if (hasChildren) {
            for (const child of node.children!) {
                walk(child, currentCategoryParentId);
            }
        }
    }

    // Root starts as id "0" because nextId starts at 0
    walk(root, null);

    // Now resolve labels for element nodes (null category, has localId)
    if (elementLocalIds.length) {
        // This is the actual API from FragmentsModel
        const itemsData = await model.getItemsData(elementLocalIds);

        // Assuming order of results matches order of input
        const dataByLocalId = new Map<number, FRAGS.ItemData>();
        itemsData.forEach((data, idx) => {
            const localId = elementLocalIds[idx];
            dataByLocalId.set(localId, data);
        });

        for (const node of flat) {
            if (!node.isCategory && node.localId != null) {
                const data = dataByLocalId.get(node.localId);
                if (data) {
                    const anyData = data as any;
                    const name = anyData?.Name && 'value' in anyData.Name ? String(anyData.Name.value) : anyData?.LongName && 'value' in anyData.LongName ? String(anyData.LongName.value) : `#${node.localId}`;

                    node.label = name;
                } else {
                    node.label = `#${node.localId}`;
                }
            }
        }
    }

    return flat;
}
export interface FlatNode {
    id: string | number;
    parentId: string | number | null;
    category: string;
}
